import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";

import styles from "./styles";

interface Props {
  title: string;
  time: string;
  description: string;
  explanation: string;
  buttonTitle: string;
  onClick: () => void;
  icon: string;
  body?: JSX.Element;
  completed?: boolean;
  locked?: boolean;
  onRefresh?: () => void;
  refreshLoading?: boolean;
}

export const OnboardingCard = ({
  title,
  time,
  description,
  explanation,
  buttonTitle,
  onClick,
  icon,
  body = undefined,
  completed = false,
  locked = false,
  onRefresh,
  refreshLoading = false,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <Box
      sx={[
        styles.onboardingCard,
        completed && {
          background: "#F0FDF4",
          borderColor: "#B9F8CF",
        },
        locked && { opacity: 0.5 },
      ]}
    >
      {/* Icon circle */}
      {!isMobileScreen && (
        <Box
          sx={[
            styles.onboardingIconCircle,
            completed && { background: "#00C950" },
          ]}
        >
          <Box
            component="i"
            className={completed ? "fa-solid fa-circle-check" : icon}
            sx={{ fontSize: 20, color: completed ? "white" : "#4338CA" }}
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography
            fontWeight={600}
            fontSize="1rem"
            color="#101828"
            lineHeight="24px"
          >
            {title}
          </Typography>
          {!completed && (
            <Box sx={{ display: "flex" }}>
              <Box sx={styles.onboardingBadge}>
                <Typography
                  fontWeight={500}
                  fontSize="0.75rem"
                  color="#432DD7"
                  lineHeight="16px"
                  whiteSpace="nowrap"
                >
                  {time} min
                </Typography>
              </Box>
              {onRefresh && (
                <>
                  {refreshLoading && !locked ? (
                    <CircularProgress size={12} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={onRefresh}
                      sx={{ ml: "auto" }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-rotate-right"
                        sx={{ fontSize: 14, color: "#4A5565" }}
                      />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Subtitle */}
        {!completed && (
          <Typography
            fontSize="0.875rem"
            color="#4A5565"
            lineHeight="20px"
            mb={1.5}
          >
            {description}
          </Typography>
        )}

        {/* Why this matters box */}
        {!completed && (
          <Box sx={styles.onboardingWhyBox}>
            <Box
              component="i"
              className="fa-regular fa-lightbulb"
              sx={{ fontSize: 14, color: "#101828", mt: "1px", flexShrink: 0 }}
            />
            <Typography fontSize="0.75rem" lineHeight="16px" color="#101828">
              <Box component="span" fontWeight={700}>
                Why this matters:{" "}
              </Box>

              {explanation}
            </Typography>
          </Box>
        )}

        {body}

        {/* CTA button */}
        {completed ? (
          <Box
            sx={{
              borderColor: "#86EFAC",
              cursor: "default",
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Typography
              fontWeight={500}
              fontSize="0.875rem"
              lineHeight="20px"
              sx={{ color: "green.main" }}
            >
              Completed
            </Typography>
          </Box>
        ) : (
          <StyledLargeButton
            fullWidth
            styles={{
              ...styles.onboardingCtaButton,
              ...(locked && {
                cursor: "not-allowed",
              }),
            }}
            onClick={locked ? undefined : onClick}
          >
            <Typography
              fontWeight={500}
              fontSize="0.875rem"
              color={"white"}
              lineHeight="20px"
            >
              {buttonTitle}
            </Typography>
            <Box
              component="i"
              className={
                locked ? "fa-regular fa-lock" : "fa-regular fa-chevron-right"
              }
              sx={{
                ...styles.onboardingCtaIcon,
                ml: 0.5,
                color: "white",
              }}
            />
          </StyledLargeButton>
        )}
      </Box>
    </Box>
  );
};

export default OnboardingCard;
